import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "src/features/auth/axiosConfig";
import { toast } from "react-toastify";
import { useUser } from "src/shared/context/UserContext";
import CommonTable, { HeadCell } from "src/business-types/grocery/components/CommonTable";
import { useUserTheme } from "src/shared/hooks/useUserTheme";

export interface CustomerDetails {
  id: string;
  Name: string;
  phone_number: string;
  s_no: number;
}

interface CustomerPhone {
  phone: string;
  isPrimary?: boolean;
}

interface CustomerResponse {
  _id: { $oid: string };
  firstName?: string;
  middleName?: string;
  lastName?: string;
  phones?: CustomerPhone[];
  emails?: string[];
  addresses?: {
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    postalCode?: string;
  };
}

interface ApiResponse {
  customer_details: CustomerResponse[];
  totalCount?: number;
  totalRecords?: number;
  [key: string]: any;
}

const CustomerForm: React.FC<{
  name: string;
  setName: (val: string) => void;
  phoneNumber: string;
  setPhoneNumber: (val: string) => void;
  date: string;
  setDate: (val: string) => void;
  onSubmit: () => void;
  isLoading?: boolean;
}> = ({ name, setName, phoneNumber, setPhoneNumber, date, setDate, isLoading = false }) => {
  const [customers, setCustomers] = useState<CustomerDetails[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const { userConfigration } = useUser();
  const [pageSize, setPageSize] = useState(userConfigration?.tableDefaultSize || 10);
  const navigate = useNavigate();

  const { bgClass } = useUserTheme();

  // Define table columns
  const customerHeadCells: HeadCell<CustomerDetails>[] = [
    { id: "s_no", label: "S.No", numeric: true, align: "center", sorting: false },
    { id: "Name", label: "Customer Name", numeric: false, align: "left", sorting: false },
    { id: "phone_number", label: "Phone Number", numeric: false, align: "left", sorting: false },
  ];

   const getTodayDate = () => {
    const now = new Date();
    return now.getFullYear() + "-" +  String(now.getMonth() + 1).padStart(2, "0") + "-" +  String(now.getDate()).padStart(2, "0");
  };

  const fetchCustomers = async (searchQuery: string = "") => {
    try {
      setIsLoadingCustomers(true);

      let apiUrl = `/app/pos/v2/customer?include_deleted=False&pageNumber=${pageNumber}&pageSize=${pageSize}`;

      if (searchQuery.trim()) {
        apiUrl += `&searchBy=${encodeURIComponent(searchQuery.trim())}`;
      }

      const response = await api.get<ApiResponse>(apiUrl);

      if (response.data?.customer_details) {
        const data = response.data.customer_details.map((c, i) => ({
          id: c._id?.$oid || Math.random().toString(),

          Name: [c.firstName, c.middleName, c.lastName].filter(Boolean).join(" ") || "N/A",

          phone_number: c.phones?.find((p) => p.isPrimary)?.phone || c.phones?.[0]?.phone || "N/A",

          s_no: i + 1,
        }));

        setCustomers(data);

        // Set total records from API response
        setTotalRecords(response.data.totalRecords || 0);
      } else {
        setCustomers([]);
        setTotalRecords(0);
      }
    } catch (err) {
      console.error("Error fetching customers:", err);
      toast.error("Failed to fetch customers");
      setCustomers([]);
      setTotalRecords(0);
    } finally {
      setIsLoadingCustomers(false);
    }
  };

  // Debounce search to avoid too many API calls
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    setPageNumber(1); // Reset to first page when searching

    // Clear previous timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // Set new timeout for debounced search
    const newTimeout = setTimeout(() => {
      fetchCustomers(value);
    }, 500); // 500ms delay

    setSearchTimeout(newTimeout);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setPageNumber(1); // Reset to first page when clearing search
    fetchCustomers(""); // Fetch all customers when search is cleared
  };

  const handleCustomerSelect = (id: string | number) => {
    const c = customers.find((cust) => cust.id === id);
    if (c) {
      setName(c.Name);
      setPhoneNumber(c.phone_number);
      setSelectedCustomerId(c.id);
      setSearchTerm("");
      setDate(getTodayDate());
      toast.success(`Selected customer: ${c.Name}`);
    }
  };

  const postShoppingCart = async (customerId: string) => {
    try {
      const response = await api.post("/app/pos/v2/shopping_cart", {
        Name: name,
        phone_number: phoneNumber,
        customerId: customerId,
        date: date,
      });
      navigate(`/v1/car-wash/add-car?id=${response.data._id}`);
      return response.data;
    } catch (error) {
      console.error("Error creating shopping cart:", error);
      throw error;
    }
  };

  const createCustomer = async (): Promise<string> => {
    // Split full name safely
    const nameParts = name.trim().split(/\s+/);

    const firstName = nameParts[0] || "";

    const payload = {
      firstName,
      phones: [ phoneNumber],
    };

    const res = await api.post("/app/pos/v2/customer", payload);

    return res.data._id?.$oid || res.data._id;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      let customerId: string;

      if (selectedCustomerId) {
        customerId = selectedCustomerId;
      } else {
        customerId = await createCustomer(); // always string
      }

      await postShoppingCart(customerId);

      toast.success("Shopping cart created successfully!");
    } catch (error) {
      console.error("Submission error:", error);
      toast.error("Failed to proceed");
    }
  };

  const handleNewCustomer = () => {
    setName("");
    setPhoneNumber("");
    setSelectedCustomerId(null);
    setSearchTerm("");
    setDate(getTodayDate());
    setPageNumber(1); // Reset to first page
    // Refresh the customer list to show all customers
    fetchCustomers("");
  };

  // Clean up timeout on component unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  // Initial load - fetch all customers
  useEffect(() => {
    if (!date) setDate(getTodayDate());
    fetchCustomers("");
  }, []);

  // Fetch customers when pageNumber or pageSize changes
  useEffect(() => {
    fetchCustomers(searchTerm);
  }, [pageNumber, pageSize]);

  return (
    <div className="min-h-[80vh] p-2">
      {/* Header */}
      <div className="text-center mb-6">
        <h1 className={`text-2xl font-bold py-4 ${bgClass} text-white rounded-lg mb-3`}>Car Wash Service</h1>
        <p className="text-gray-600">Find existing customers or register new vehicle for service</p>
      </div>

      {/* Main Layout */}
      <div className="w-full px-2 sm:px-4 lg:px-[2vw]">
        <div className="flex flex-col lg:flex-row gap-4 lg:gap-7">
          {/* Search Section */}
          <div className="w-full lg:flex-1 lg:max-w-full xl:max-w-[35vw] h-auto lg:h-[70vh]">
            <div className="bg-white rounded-lg shadow border border-gray-200 p-3 h-full">
              <div className="flex items-center gap-1 mb-3">
                <div className={`p-2 rounded-lg ${bgClass}`}>
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <h2 className="text-lg font-semibold text-gray-900">Find Customer</h2>
              </div>

              {/* Search Input */}
              <div className="relative mb-3">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  placeholder="Search by name or phone number..."
                  className="w-full pl-10 pr-10 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
                  <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                {searchTerm && (
                  <button type="button" onClick={handleClearSearch} className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600">
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>

              {/* Results Count */}

              {/* Table */}
              <div className="mt-2 h-64 sm:h-96 lg:h-85 overflow-auto">
                {isLoadingCustomers ? (
                  <div className="p-4 sm:p-8 text-center bg-white border border-gray-200 rounded-lg">
                    <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-2 sm:mt-3 text-gray-600 font-medium text-sm sm:text-base">{searchTerm ? "Searching customers..." : "Loading customers..."}</p>
                  </div>
                ) : (
                  <CommonTable
                    data={customers}
                    headCells={customerHeadCells}
                    title="Customers"
                    selectable={false}
                    onRowClick={handleCustomerSelect}
                    pageSize={pageSize}
                    pagenation={customers.length > pageSize}
                    showToolbar={false}
                    denseDefault={true}
                    Action={false}
                    shortByTable={false}
                    className="border border-gray-200 rounded-lg min-w-[300px]"
                  />
                )}

                {/* Pagination Controls */}
                {/* <div className="flex justify-between items-center mt-2">
                  <button
                    onClick={() => setPageNumber((prev) => Math.max(1, prev - 1))}
                    disabled={pageNumber === 1}
                    className={`px-3 py-1 rounded ${pageNumber === 1 ? "bg-gray-300 cursor-not-allowed" : "bg-blue-500 hover:bg-blue-600 text-white"}`}
                  >
                    Previous
                  </button>

                  <div className="flex flex-col items-center">
                    <span className="text-sm text-gray-600">
                      Page {pageNumber} of {Math.ceil(totalRecords / pageSize)}
                    </span>
                    <span className="text-xs text-gray-500">Total: {totalRecords.toLocaleString()} records</span>
                  </div>

                  <button
                    onClick={() => setPageNumber((prev) => prev + 1)}
                    disabled={customers.length < pageSize || pageNumber >= Math.ceil(totalRecords / pageSize)}
                    className={`px-3 py-1 rounded ${
                      customers.length < pageSize || pageNumber >= Math.ceil(totalRecords / pageSize) ? "bg-gray-300 cursor-not-allowed" : "bg-blue-500 hover:bg-blue-600 text-white"
                    }`}
                  >
                    Next
                  </button>
                </div> */}
                {/* MUI-style Pagination */}
                <div className="flex items-center justify-end gap-4 px-2 py-2 text-sm text-gray-700">
                  {/* Rows per page */}
                  <div className="flex items-center gap-2">
                    <span>Rows per page:</span>
                    <select
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setPageNumber(1);
                      }}
                      className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none"
                    >
                      {[5, 10, 25, 50].map((size) => (
                        <option key={size} value={size}>
                          {size}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Page info */}
                  <span>
                    {(pageNumber - 1) * pageSize + 1}–{Math.min(pageNumber * pageSize, totalRecords)} of {totalRecords}
                  </span>

                  {/* Navigation buttons */}
                  {/* <div className="flex items-center gap-1">
    <button
      onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
      disabled={pageNumber === 1}
      className="p-1 disabled:opacity-40"
    >
      ◀
    </button>

    <button
      onClick={() =>
        setPageNumber((p) =>
          p < Math.ceil(totalRecords / pageSize) ? p + 1 : p
        )
      }
      disabled={pageNumber >= Math.ceil(totalRecords / pageSize)}
      className="p-1 disabled:opacity-40"
    >
      ▶
    </button>
  </div> */}
                  {/* Navigation buttons */}
                  <div className="flex items-center gap-1">
                    {/* Previous */}
                    <button onClick={() => setPageNumber((p) => Math.max(1, p - 1))} disabled={pageNumber === 1} className="p-1 text-gray-600 disabled:opacity-40">
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>

                    {/* Next */}
                    <button
                      onClick={() => setPageNumber((p) => (p < Math.ceil(totalRecords / pageSize) ? p + 1 : p))}
                      disabled={pageNumber >= Math.ceil(totalRecords / pageSize)}
                      className="p-1 text-gray-600 disabled:opacity-40"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Form Section */}
          <div className="w-full lg:flex-1 lg:max-w-full xl:max-w-[45vw]">
            <div className="bg-white rounded-lg shadow border border-gray-200 p-4 sm:p-5 h-full">
              <div className="flex items-center gap-3 mb-4 sm:mb-6">
                <div className={`p-2 rounded-lg ${bgClass}`}>
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">{selectedCustomerId ? "Customer Details" : "New Service Registration"}</h2>
                  <p className="text-sm text-gray-500">{selectedCustomerId ? "Review customer information" : "Register new vehicle service"}</p>
                </div>
                {selectedCustomerId && (
                  <div className="mb-4 inline-flex items-center gap-2 px-3 text-end py-1 bg-green-100 border border-green-300 rounded-full">
                    <svg className="w-3 h-3 sm:w-4 sm:h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-xs sm:text-sm font-medium text-green-800">Selected: {name}</span>
                    <button type="button" onClick={handleNewCustomer} className="ml-2 text-xs text-green-700 hover:text-green-900 underline">
                      Clear
                    </button>
                  </div>
                )}
              </div>

              <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
                <div className="w-full">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <input
                    type="tel"
                    value={phoneNumber}
                    placeholder="Enter phone number (8-12 digits)"
                    onChange={(e) => {
                      const input = e.target.value.replace(/\D/g, "");
                      if (input.length <= 12) setPhoneNumber(input);
                      if (selectedCustomerId) setSelectedCustomerId(null);
                    }}
                    required
                    pattern="\d{8,12}"
                    minLength={8}
                    maxLength={12}
                    disabled={!!selectedCustomerId} // Disable when existing customer is selected
                    className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-500 transition-colors text-gray-900 placeholder-gray-400 ${
                      selectedCustomerId ? "bg-gray-100 cursor-not-allowed" : "bg-white"
                    }`}
                  />
                  {selectedCustomerId && <p className="text-xs text-gray-500 mt-1">Phone number cannot be edited for existing customers</p>}
                </div>

                <div className="w-full">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Customer Name</label>
                  <input
                    type="text"
                    value={name}
                    placeholder="Enter customer full name"
                    onChange={(e) => {
                      setName(e.target.value);
                      if (selectedCustomerId) setSelectedCustomerId(null);
                    }}
                    disabled={!!selectedCustomerId} // Disable when existing customer is selected
                    className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-500 transition-colors text-gray-900 placeholder-gray-400 ${
                      selectedCustomerId ? "bg-gray-100 cursor-not-allowed" : "bg-white"
                    }`}
                  />
                  {selectedCustomerId && <p className="text-xs text-gray-500 mt-1">Customer name cannot be edited for existing customers</p>}
                </div>

                <div className="w-full">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Service Date</label>
                  <input
                    type="date"
                    value={date}
                    min={getTodayDate()}
                    onChange={(e) => setDate(e.target.value)}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-500 transition-colors text-gray-900 placeholder-gray-400 bg-white"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className={`w-full py-2 sm:py-3 px-4 rounded-lg font-semibold text-white transition-colors text-sm sm:text-base
                    ${isLoading ? "bg-gray-400 cursor-not-allowed" : `${bgClass} hover:opacity-90`}`}
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-3 w-3 sm:h-4 sm:w-4 border-b-2 border-white"></div>
                      Processing...
                    </div>
                  ) : selectedCustomerId ? (
                    `Continue with ${name}`
                  ) : (
                    "Register New Service"
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerForm;
